import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { Asset } from 'contentful';
import styled from 'styled-components';
import useContentful from './hooks/useContentful';

const Heading = styled.h3`
  border-bottom: 1px solid #eaeaea;
  color: #1a1b1b;
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 0;
  padding: 0 0 1rem;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 1.5rem;

  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

interface TileContentProps {
  backgroundimageurl: string;
  themetype: string;
}

const TileLink = styled.a`
  flex: 1 1 100%;
  display: block;
  text-decoration: none;
  color: inherit;

  @media (min-width: 992px) {
    flex: 1 1 calc(31.25% - 0.75rem);
    &:not(:last-child) {
      margin-right: 1.5rem;
    }
  }
`;

const TileContent = styled.div<TileContentProps>`
  align-items: center;
  background-image: url(${props => props.backgroundimageurl});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 1.5rem;
  text-align: center;
  aspect-ratio: 254 / 317;
  margin-bottom: 1.5rem;

  @media (min-width: 992px) {
    margin-bottom: 0;
  }
`;

const SecondaryHeading = styled.h3<{ themetype: string }>`
  color: ${props => (props.themetype === 'dark' ? '#fff' : '#1a1b1b')};
  font-size: 1.5rem;
  line-height: 1.75rem;
  margin: 0 0 0.5rem;
`;

const Description = styled.p<{ themetype: string }>`
  color: ${props => (props.themetype === 'dark' ? '#fff' : '#1a1b1b')};
  font-size: 0.875rem;
  line-height: 1.375rem;
  margin: 0 0 1rem;
`;

const Button = styled.div<{ themetype: string }>`
  background-color: ${props => (props.themetype === 'dark' ? '#fff' : '#000')};
  border-color: ${props => (props.themetype === 'dark' ? '#fff' : '#000')};
  color: ${props => (props.themetype === 'dark' ? '#000' : '#fff')};
  border-radius: 1.875rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 0;
  padding: 0 1.875rem;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 0.2s ease;
  min-width: 7.5rem;
  min-height: 2.8125rem;
  letter-spacing: normal;
  font-size: 0.75rem;
`;

interface QuickAccessProps {
  data: {
    button1Text?: string;
    button2Text?: string;
    heading1?: string;
    heading2?: string;
    image1?: Asset;
    image2?: Asset;
    title1?: string;
    title2?: string;
    button1Link?: string;
    button2Link?: string;
    mainHeading?: string;
    theme1?: boolean;
    theme2?: boolean;
  };
}

const QuickAccess: FC<QuickAccessProps> = ({ data }) => {
  const backgroundimageurl1 = data.image1?.fields.file?.url ? `https:${data.image1.fields.file.url}` : '';
  const backgroundimageurl2 = data.image2?.fields.file?.url ? `https:${data.image2.fields.file.url}` : '';

  const theme1 = data.theme1 ? 'dark' : 'light';
  const theme2 = data.theme2 ? 'dark' : 'light';

  return (
    <>
      <Heading>Quick Access</Heading>
      <Wrapper>
        <TileLink href={data.button1Link}>
          <TileContent backgroundimageurl={backgroundimageurl1} themetype={theme1}>
            <SecondaryHeading themetype={theme1}>{data.title1}</SecondaryHeading>
            <Description themetype={theme1}>{data.heading1}</Description>
            <Button themetype={theme1}>{data.button1Text}</Button>
          </TileContent>
        </TileLink>

        <TileLink href={data.button2Link}>
          <TileContent backgroundimageurl={backgroundimageurl2} themetype={theme2}>
            <SecondaryHeading themetype={theme2}>{data.title2}</SecondaryHeading>
            <Description themetype={theme2}>{data.heading2}</Description>
            <Button themetype={theme2}>{data.button2Text}</Button>
          </TileContent>
        </TileLink>
      </Wrapper>
    </>
  );
};

const Index: FC = () => {
  const contentfulDiv = document.getElementById('search-quick-access');
  const entryId = contentfulDiv?.getAttribute('data-entry-id');
  const locale = contentfulDiv?.getAttribute('data-locale');

  if (entryId && locale) {
    const { contentfulData } = useContentful({ include: 10, 'sys.id': entryId, locale: locale });

    if (contentfulData) {
      const QuickAccessData = contentfulData?.items[0]?.fields;
      if (QuickAccessData) {
        return <QuickAccess data={QuickAccessData} />;
      }
    }
  }

  return <div>No Content Found</div>;
};

ReactDOM.render(<Index />, document.getElementById('search-quick-access'));
